import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useClient } from "../context/ClientContext";
import { PageHeader, Section, Card, CardHeader, Pill, LoadingBar } from "../components/Primitives";
import { CircleNotch, CheckCircle, XCircle } from "@phosphor-icons/react";

const STATUS = {
  running: { tone: "ai", icon: <CircleNotch size={11} className="animate-spin mr-1" /> },
  passed: { tone: "success", icon: <CheckCircle size={11} className="mr-1" /> },
  failed: { tone: "danger", icon: <XCircle size={11} className="mr-1" /> },
};

function fmt(d) {
  if (!d) return "—";
  const dt = new Date(d);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

export default function Orchestration() {
  const { activeClient, activeClientId } = useClient();
  const [runs, setRuns] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeClientId) return;
    setLoading(true);
    const params = { params: { client_id: activeClientId } };
    Promise.all([api.get("/orchestration/runs", params), api.get("/orchestration/frameworks", params)]).then(([a, b]) => {
      setRuns(a.data); setFrameworks(b.data); setLoading(false);
    });
  }, [activeClientId]);

  if (loading) return <LoadingBar label="Connecting to orchestration fabric" />;

  return (
    <div data-testid="orchestration-page">
      <PageHeader
        overline={`Layer 5 · ${activeClient?.name || ""}`}
        title="Automation Orchestration"
        description="Single execution fabric across heterogeneous frameworks. Centralised runs, environments, and reporting."
      />
      <Section>
        <div className="grid grid-cols-12 gap-6">
          <Card className="col-span-12 lg:col-span-4" testid="frameworks-card">
            <CardHeader overline="Connected frameworks" title="Execution Fabric" />
            <div className="px-6 py-4 space-y-3">
              {frameworks.map((f) => (
                <div key={f.id} className="flex items-center justify-between border-b border-zinc-100 pb-3 last:border-0 last:pb-0">
                  <div>
                    <div className="text-sm text-zinc-900 font-medium">{f.name}</div>
                    <div className="text-xs text-zinc-500 font-mono">v{f.version} · {f.tests} tests</div>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-emerald-700">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> connected
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="col-span-12 lg:col-span-8" testid="runs-card">
            <CardHeader overline="Run console" title="Recent Executions" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="runs-table">
                <thead>
                  <tr>
                    {["Run", "Framework", "Environment", "Status", "Pass", "Fail", "Started"].map((h) => (
                      <th key={h} className="text-left px-6 py-3 overline text-zinc-500 border-b border-zinc-200">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => {
                    const st = STATUS[r.status] || STATUS.passed;
                    return (
                      <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="px-6 py-3 text-zinc-900">{r.name}</td>
                        <td className="px-6 py-3 text-zinc-600 font-mono text-xs">{r.framework}</td>
                        <td className="px-6 py-3 text-zinc-600 font-mono text-xs">{r.environment}</td>
                        <td className="px-6 py-3"><Pill tone={st.tone}>{st.icon}{r.status}</Pill></td>
                        <td className="px-6 py-3 font-mono text-emerald-700">{r.tests_passed}</td>
                        <td className="px-6 py-3 font-mono text-rose-700">{r.tests_failed}</td>
                        <td className="px-6 py-3 text-zinc-500 text-xs font-mono">{fmt(r.started_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </Section>
    </div>
  );
}
